/**
 * Label previews that look like the labels they stand in for.
 *
 * A USPS Priority label and a UPS Ground label are laid out differently enough
 * that a generic box helps nobody: the point of a preview is that someone
 * glances at it and recognises what is about to come out of the printer. So the
 * structure here follows the real thing — USPS's black service band, tracking
 * number under the barcode, recipient in caps; UPS's brown header, routing code,
 * MaxiCode square, 1Z number.
 *
 * What is deliberately *not* replicated: the barcodes, and the carrier logos.
 *
 * The barcodes cannot be real — there is no tracking number until the label is
 * bought — and drawing a convincing fake is worse than leaving a marked gap.
 * Someone would eventually print this instead of the real label, and a parcel
 * with an unscannable barcode is a parcel that stops moving. They stay as
 * labelled zones.
 *
 * The logos are the carriers' marks, not ours to redraw. The real label from
 * Shippo carries them; this shows the carrier's name in its own colours, which
 * is enough to tell a Priority label from a Ground one at a glance.
 */

/**
 * Scale drawing so previews match the real media.
 *
 * A fixed pixels-per-inch means a #10 envelope and a 4x6 label differ on screen
 * the way they differ in the hand — which is the point of showing a preview
 * before printing.
 */
const PIXELS_PER_INCH = 62;
const MAX_WIDTH_PX = 470;

function scaleFor(widthInches, heightInches) {
  const naturalWidth = widthInches * PIXELS_PER_INCH;
  const scale = naturalWidth > MAX_WIDTH_PX ? MAX_WIDTH_PX / naturalWidth : 1;

  return {
    width: naturalWidth * scale,
    height: heightInches * PIXELS_PER_INCH * scale,
  };
}

function normalizeCarrier(carrier) {
  const name = (carrier ?? '').toLowerCase();
  if (name.includes('usps')) return 'usps';
  if (name.includes('ups')) return 'ups';
  if (name.includes('fedex')) return 'fedex';
  if (name.includes('dhl')) return 'dhl';
  return 'other';
}

function fromAddress(returnAddress) {
  if (!returnAddress) return null;
  return {
    name: returnAddress.contactName || returnAddress.name,
    address1: returnAddress.address1,
    address2: returnAddress.address2,
    city: returnAddress.city,
    state: returnAddress.state,
    zip: returnAddress.zip,
  };
}

function AddressLines({ address, className, uppercase }) {
  if (!address) return null;

  const cityLine = [address.city, address.city && address.state ? ', ' : '', address.state]
    .join('')
    .concat(address.zip ? ` ${address.zip}` : '');

  return (
    <div className={className}>
      <div className="font-semibold">{uppercase ? address.name?.toUpperCase() : address.name}</div>
      <div>{uppercase ? address.address1?.toUpperCase() : address.address1}</div>
      {address.address2 && <div>{uppercase ? address.address2.toUpperCase() : address.address2}</div>}
      <div>{uppercase ? cityLine.toUpperCase() : cityLine}</div>
    </div>
  );
}

function Placeholder({ children }) {
  return <div className="text-[9px] text-slate-400 italic">{children}</div>;
}

/**
 * A marked gap where the carrier's barcode will print.
 *
 * Bars are drawn evenly on purpose. A real barcode has irregular bar widths, and
 * anything that looked scannable here would sooner or later get printed and
 * shipped.
 */
function BarcodeZone({ height = 34, label }) {
  return (
    <div>
      <div
        className="bg-[repeating-linear-gradient(90deg,#0f172a_0,#0f172a_2px,transparent_2px,transparent_5px)] opacity-25"
        style={{ height: `${height}px` }}
      />
      {label && (
        <div className="text-[6px] uppercase tracking-widest text-slate-400 text-center mt-0.5">
          {label}
        </div>
      )}
    </div>
  );
}

/** UPS puts a 2D MaxiCode beside the routing block. Same reasoning as above. */
function MaxicodeZone() {
  return (
    <div className="w-[46px] h-[46px] border border-slate-300 shrink-0 flex items-center justify-center bg-[repeating-conic-gradient(#0f172a_0_25%,transparent_0_50%)] bg-[length:6px_6px] opacity-30">
      <span className="sr-only">MaxiCode prints here</span>
    </div>
  );
}

function LabelShell({ width, height, children }) {
  return (
    <div
      className="bg-white border border-slate-400 shadow-md relative flex flex-col overflow-hidden"
      style={{ width: `${width}px`, height: `${height}px` }}
    >
      {children}
    </div>
  );
}

function PreviewFrame({ children, note }) {
  return (
    <div className="flex flex-col items-center gap-2 p-6 bg-slate-50 rounded-lg">
      {children}
      <p className="text-[10px] text-slate-500 text-center max-w-[85%] leading-snug">{note}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// USPS
// ---------------------------------------------------------------------------

function UspsLabel({ from, recipient, serviceName, weightLbs, referenceNumber, width, height }) {
  // USPS names the class in the band: Priority, Ground Advantage, First-Class.
  const band = (serviceName || 'USPS').toUpperCase();

  return (
    <LabelShell width={width} height={height}>
      <div className="bg-slate-900 text-white px-2 py-1.5 flex items-center justify-between">
        <span className="font-extrabold text-[13px] tracking-tight leading-none">{band}</span>
        <span className="text-[7px] leading-tight text-right opacity-90">
          U.S. POSTAGE
          <br />
          PAID
        </span>
      </div>

      <div className="px-2 pt-1.5 pb-1 border-b border-slate-300">
        <div className="text-[6px] uppercase tracking-widest text-slate-500">From</div>
        {from ? (
          <AddressLines address={from} className="text-[7px] leading-[1.15] text-slate-800" />
        ) : (
          <Placeholder>Return address</Placeholder>
        )}
      </div>

      <div className="px-2 py-2 flex-1 flex gap-1.5">
        <div className="text-[7px] font-bold tracking-tight text-slate-900 leading-tight pt-0.5">
          SHIP
          <br />
          TO:
        </div>
        <div className="flex-1">
          {recipient?.name ? (
            /* Caps for the delivery address is the USPS convention, and the
               reason their labels read the way they do at arm's length. */
            <AddressLines
              address={recipient}
              uppercase
              className="text-[10px] leading-[1.2] text-slate-900 font-semibold"
            />
          ) : (
            <Placeholder>Delivery address appears here</Placeholder>
          )}
        </div>
      </div>

      <div className="px-2 pb-0.5 flex items-end justify-between text-[6px] text-slate-500">
        <span>{weightLbs > 0 ? `${weightLbs.toFixed(1)} LB` : ''}</span>
        {referenceNumber && <span className="truncate max-w-[55%]">REF {referenceNumber}</span>}
      </div>

      <div className="border-t-2 border-slate-900 px-2 py-1.5">
        <div className="text-[6px] font-bold tracking-widest text-slate-700 mb-0.5">
          USPS TRACKING #
        </div>
        <BarcodeZone height={32} />
        <div className="text-[7px] tracking-[0.15em] text-slate-400 text-center mt-0.5">
          9400 1000 0000 0000 0000 00
        </div>
      </div>
    </LabelShell>
  );
}

// ---------------------------------------------------------------------------
// UPS
// ---------------------------------------------------------------------------

function UpsLabel({ from, recipient, serviceName, weightLbs, referenceNumber, width, height }) {
  // The big routing line on a UPS label is derived from the destination; the
  // state and ZIP are the parts a person actually recognises.
  const routing = recipient?.state
    ? `${recipient.state.toUpperCase()} ${(recipient.zip || '').slice(0, 3)}`
    : '';

  return (
    <LabelShell width={width} height={height}>
      <div className="bg-[#351c15] text-[#ffb500] px-2 py-1.5 flex items-center justify-between">
        <span className="font-extrabold text-[13px] tracking-tight leading-none">UPS</span>
        <span className="text-[7px] text-right leading-tight text-white/85">
          {weightLbs > 0 ? `${weightLbs.toFixed(1)} LBS` : ''}
          <br />
          {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase()}
        </span>
      </div>

      <div className="px-2 pt-1.5 pb-1 border-b border-slate-300">
        <div className="text-[6px] uppercase tracking-widest text-slate-500">Ship From</div>
        {from ? (
          <AddressLines address={from} className="text-[7px] leading-[1.15] text-slate-800" />
        ) : (
          <Placeholder>Return address</Placeholder>
        )}
      </div>

      <div className="px-2 py-1.5 border-b border-slate-300">
        <div className="text-[6px] uppercase tracking-widest text-slate-500">Ship To</div>
        {recipient?.name ? (
          <AddressLines
            address={recipient}
            uppercase
            className="text-[9px] leading-[1.2] text-slate-900 font-semibold"
          />
        ) : (
          <Placeholder>Delivery address appears here</Placeholder>
        )}
      </div>

      <div className="px-2 py-1.5 flex items-center gap-2 flex-1">
        <MaxicodeZone />
        <div className="flex-1 text-right">
          <div className="text-[15px] font-extrabold tracking-tight text-slate-900 leading-none">
            {routing || '—'}
          </div>
          {referenceNumber && (
            <div className="text-[6px] text-slate-500 mt-1 truncate">REF {referenceNumber}</div>
          )}
        </div>
      </div>

      <div className="bg-slate-900 text-white px-2 py-1 text-center font-extrabold text-[10px] tracking-wide">
        {(serviceName || 'UPS').toUpperCase()}
      </div>

      <div className="px-2 py-1.5">
        <div className="text-[6px] font-bold tracking-widest text-slate-700 mb-0.5">
          TRACKING #: 1Z 000 000 00 0000 000 0
        </div>
        <BarcodeZone height={28} />
      </div>
    </LabelShell>
  );
}

// ---------------------------------------------------------------------------
// Anything else
// ---------------------------------------------------------------------------

const OTHER_STYLES = {
  fedex: { bar: 'bg-[#4d148c]', text: 'text-white', accent: '#ff6600' },
  dhl: { bar: 'bg-[#ffcc00]', text: 'text-[#d40511]', accent: '#d40511' },
  other: { bar: 'bg-slate-900', text: 'text-white', accent: '#0f172a' },
};

function GenericLabel({ carrier, from, recipient, serviceName, weightLbs, width, height }) {
  const style = OTHER_STYLES[normalizeCarrier(carrier)] ?? OTHER_STYLES.other;

  return (
    <LabelShell width={width} height={height}>
      <div className={`${style.bar} ${style.text} px-2 py-1.5 flex items-center justify-between`}>
        <span className="font-extrabold text-[13px] tracking-tight leading-none">
          {(carrier || 'CARRIER').toUpperCase()}
        </span>
        <span className="text-[7px] opacity-90">
          {weightLbs > 0 ? `${weightLbs.toFixed(1)} LB` : ''}
        </span>
      </div>

      <div className="px-2 pt-1.5 pb-1 border-b border-slate-300">
        <div className="text-[6px] uppercase tracking-widest text-slate-500">From</div>
        {from ? (
          <AddressLines address={from} className="text-[7px] leading-[1.15] text-slate-800" />
        ) : (
          <Placeholder>Return address</Placeholder>
        )}
      </div>

      <div className="px-2 py-2 flex-1">
        <div className="text-[6px] uppercase tracking-widest text-slate-500 mb-0.5">Ship to</div>
        {recipient?.name ? (
          <AddressLines
            address={recipient}
            uppercase
            className="text-[10px] leading-[1.2] text-slate-900 font-semibold"
          />
        ) : (
          <Placeholder>Delivery address appears here</Placeholder>
        )}
      </div>

      <div className="px-2 py-1 text-[7px] font-bold text-slate-700 border-t border-slate-300">
        {serviceName || ''}
      </div>

      <div className="px-2 pb-1.5">
        <BarcodeZone height={30} label="tracking barcode" />
      </div>
    </LabelShell>
  );
}

// ---------------------------------------------------------------------------

export function ShippingLabelPreview({
  returnAddress,
  recipient,
  carrier,
  serviceName,
  weightLbs = 0,
  referenceNumber,
  widthInches,
  heightInches,
}) {
  const { width, height } = scaleFor(widthInches, heightInches);
  const from = fromAddress(returnAddress);
  const props = { from, recipient, serviceName, weightLbs, referenceNumber, width, height };

  const kind = normalizeCarrier(carrier);
  const label =
    kind === 'usps' ? (
      <UspsLabel {...props} />
    ) : kind === 'ups' ? (
      <UpsLabel {...props} />
    ) : (
      <GenericLabel carrier={carrier} {...props} />
    );

  return (
    <PreviewFrame note="A preview of the layout and addresses. The real label, with its barcode and tracking number, is created when you buy it.">
      {label}
    </PreviewFrame>
  );
}

/** Return address top-left, delivery address centred — standard envelope layout. */
export function EnvelopePreview({ returnAddress, recipient, widthInches, heightInches }) {
  const { width, height } = scaleFor(widthInches, heightInches);
  const from = fromAddress(returnAddress);

  return (
    <PreviewFrame note="A preview of where things print. The postage indicia is added when you buy it.">
      <div
        className="bg-white border border-slate-400 shadow-md relative"
        style={{ width: `${width}px`, height: `${height}px` }}
      >
        {from ? (
          <AddressLines
            address={from}
            className="absolute top-3 left-3 text-[8px] leading-[1.15] text-slate-700 max-w-[40%]"
          />
        ) : (
          <div className="absolute top-3 left-3">
            <Placeholder>Return address</Placeholder>
          </div>
        )}

        {/* Where USPS meter postage prints, top-right, as on a franked envelope. */}
        <div className="absolute top-3 right-3 w-[58px] h-[38px] border border-dashed border-slate-300 flex flex-col items-center justify-center leading-tight">
          <span className="text-[6px] font-bold text-slate-500">U.S. POSTAGE</span>
          <span className="text-[6px] font-bold text-slate-500">PAID</span>
          <span className="text-[5px] text-slate-400 mt-0.5">prints here</span>
        </div>

        {recipient?.name ? (
          <AddressLines
            address={recipient}
            uppercase
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/4 text-[10px] leading-[1.25] text-slate-900 text-left font-medium"
          />
        ) : (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <Placeholder>Delivery address appears here</Placeholder>
          </div>
        )}
      </div>
    </PreviewFrame>
  );
}
